import {
  JSNS,
  type Jsns,
  type JsonObject,
  type SoapRequestContext,
  type SoapRequestEnvelope,
  type SoapResponseContext,
} from '@zimbra-webif/types';
import { ZimbraError } from './errors';
import { NotificationHub } from './notifications';
import { SoapTransport, type FetchLike } from './transport';
import { AuthApi } from './domains/auth';
import { MailApi } from './domains/mail';
import { ComposeApi } from './domains/compose';
import { FiltersApi } from './domains/filters';
import { CalendarApi } from './domains/calendar';
import { TasksApi } from './domains/tasks';
import { ContactsApi } from './domains/contacts';
import { GalApi } from './domains/gal';
import { PrefsApi } from './domains/prefs';
import { IdentitiesApi } from './domains/identities';
import { AccountApi } from './domains/account';
import { ShareApi } from './domains/share';
import { BriefcaseApi } from './domains/briefcase';
import { DataSourcesApi } from './domains/datasources';
import { SyncApi } from './domains/sync';

export interface ZimbraClientOptions {
  /** SOAP origin/path prefix (see TransportOptions.baseUrl). */
  baseUrl: string;
  fetch?: FetchLike;
  /** Pre-existing auth token (e.g. injected by the BFF). */
  authToken?: string;
  /** Maintain a notification session (long-poll channel). Default: true. */
  session?: boolean;
  /** Extra headers (e.g. CSRF) for every request. */
  headers?: Record<string, string>;
}

/** One batched sub-request. */
export interface BatchCall {
  jsns: Jsns;
  /** Command base name, e.g. "GetFolder" (the SDK appends "Request"). */
  command: string;
  params?: JsonObject;
}

/**
 * The Zimbra client: typed entry point to the JSON-SOAP API.
 *
 * Holds the auth token + notification session, builds envelopes, dispatches
 * single calls and batches, and threads the change-notification channel. Domain
 * surfaces hang off it (`client.auth`, `client.mail`, …) — one typed method per
 * SOAP command, no GraphQL indirection.
 */
export class ZimbraClient {
  readonly transport: SoapTransport;
  readonly notifications = new NotificationHub();

  readonly auth: AuthApi = new AuthApi(this);
  readonly mail: MailApi = new MailApi(this);
  readonly compose: ComposeApi = new ComposeApi(this);
  readonly filters: FiltersApi = new FiltersApi(this);
  readonly calendar: CalendarApi = new CalendarApi(this);
  readonly tasks: TasksApi = new TasksApi(this);
  readonly contacts: ContactsApi = new ContactsApi(this);
  readonly gal: GalApi = new GalApi(this);
  readonly prefs: PrefsApi = new PrefsApi(this);
  readonly identities: IdentitiesApi = new IdentitiesApi(this);
  readonly account: AccountApi = new AccountApi(this);
  readonly share: ShareApi = new ShareApi(this);
  readonly briefcase: BriefcaseApi = new BriefcaseApi(this);
  readonly datasources: DataSourcesApi = new DataSourcesApi(this);
  readonly sync: SyncApi = new SyncApi(this);

  private authToken: string | undefined;
  private sessionId: string | undefined;
  private highestNotifySeq: number | undefined;
  private readonly useSession: boolean;

  constructor(opts: ZimbraClientOptions) {
    this.transport = new SoapTransport({
      baseUrl: opts.baseUrl,
      ...(opts.fetch ? { fetch: opts.fetch } : {}),
      ...(opts.headers ? { headers: opts.headers } : {}),
    });
    this.authToken = opts.authToken;
    this.useSession = opts.session ?? true;
  }

  setAuthToken(token: string | undefined): void {
    this.authToken = token;
  }

  getAuthToken(): string | undefined {
    return this.authToken;
  }

  /** Invoke a single SOAP command and return its typed `<Name>Response` body. */
  async call<TRes extends JsonObject = JsonObject>(
    jsns: Jsns,
    command: string,
    params: JsonObject = {},
  ): Promise<TRes> {
    const envelope = this.buildEnvelope({
      [`${command}Request`]: { _jsns: jsns, ...params },
    });
    const response = await this.transport.post(envelope);
    this.absorbContext(response.Header?.context);
    if (response.Body.Fault) {
      throw new ZimbraError(response.Body.Fault);
    }
    return response.Body[`${command}Response`] as TRes;
  }

  /**
   * Invoke several commands in one round-trip via BatchRequest.
   *
   * Each sub-request gets a `requestId` (its index as a string) so callers can
   * correlate entries in `BatchResponse`. `onerror` controls whether the server
   * stops at the first fault or continues.
   */
  async batch(
    calls: BatchCall[],
    onerror: 'continue' | 'stop' = 'continue',
  ): Promise<JsonObject> {
    const batchReq: JsonObject = { _jsns: JSNS.all, onerror };
    calls.forEach((c, i) => {
      const name = `${c.command}Request`;
      const entry: JsonObject = { _jsns: c.jsns, requestId: String(i), ...(c.params ?? {}) };
      const existing = batchReq[name];
      if (Array.isArray(existing)) {
        existing.push(entry);
      } else if (existing) {
        batchReq[name] = [existing, entry];
      } else {
        batchReq[name] = entry;
      }
    });

    const envelope = this.buildEnvelope({ BatchRequest: batchReq });
    const response = await this.transport.post(envelope);
    this.absorbContext(response.Header?.context);
    if (response.Body.Fault) {
      throw new ZimbraError(response.Body.Fault);
    }
    return response.Body.BatchResponse as JsonObject;
  }

  private buildEnvelope(body: JsonObject): SoapRequestEnvelope {
    const context: SoapRequestContext = { _jsns: JSNS.all };
    if (this.authToken) {
      context.authToken = this.authToken;
    }
    if (this.useSession) {
      context.session = this.sessionId ? { id: this.sessionId } : {};
      if (this.highestNotifySeq !== undefined) {
        context.notify = { seq: this.highestNotifySeq };
      }
    }
    return { Header: { context }, Body: body };
  }

  private absorbContext(context: SoapResponseContext | undefined): void {
    if (!context) {
      return;
    }
    const id = context.session?.id ?? context.session?._content;
    if (id) {
      this.sessionId = id;
    }
    const maxSeq = this.notifications.ingest(context);
    if (maxSeq !== undefined) {
      this.highestNotifySeq = maxSeq;
    }
  }
}
